import React, { type FC } from 'react'

import { type IntlShape, useIntl } from 'react-intl'

import { Flex, TEXT_VARIANT_LIGHT, Text } from 'mailup-web-components'

import IconStarRegular from 'Components/icons/IconStarRegular'
import IconStarSolid from 'Components/icons/IconStarSolid'

import RenderWhen from 'Components/RenderWhen'
import type { EngagementScore } from 'Pages/RecipientsList/model'

interface EngagementViewProps {
  score: EngagementScore
}

const STAR_COLOR_ON = '#16a7cd'
const STAR_COLOR_OFF = '#cbcbcb'

const getTip = (intl: IntlShape, score: EngagementScore): string => {
  switch (score) {
    case 'New':
      return intl.formatMessage({
        id: 'Load.RecipientsList.EngagementNewTooltip',
      })

    case 'Inactive':
      return intl.formatMessage({
        id: 'Load.RecipientsList.EngagementInactiveTooltip',
      })

    case 'LessActive':
      return intl.formatMessage({
        id: 'Load.RecipientsList.EngagementLessActiveTooltip',
      })

    case 'Active':
      return intl.formatMessage({
        id: 'Load.RecipientsList.EngagementActiveTooltip',
      })

    case 'VeryActive':
      return intl.formatMessage({
        id: 'Load.RecipientsList.EngagementVeryActiveTooltip',
      })

    case 'Abandoned':
    default:
      return intl.formatMessage({
        id: 'Load.RecipientsList.EngagementAbandonedTooltip',
      })
  }
}

const EngagementView: FC<EngagementViewProps> = ({ score }) => {
  const intl = useIntl()

  if (score === 'Abandoned') {
    return (
      <Text
        htmlElement="span"
        variant={TEXT_VARIANT_LIGHT}
        data-tip={getTip(intl, score)}
      >
        -
      </Text>
    )
  }

  const stars = Array.from({ length: 3 })

  const size = {
    width: '15px',
    height: '15px',
  }

  return (
    <Flex
      display="inline-flex"
      alignItems="center"
      height="20px"
      data-tip={getTip(intl, score)}
    >
      <RenderWhen condition={score === 'New'}>
        {stars.map((_, i) => (
          <IconStarRegular key={i} fill={STAR_COLOR_OFF} {...size} />
        ))}
      </RenderWhen>

      <RenderWhen condition={score === 'Inactive'}>
        {stars.map((_, i) => (
          <IconStarSolid key={i} fill={STAR_COLOR_OFF} {...size} />
        ))}
      </RenderWhen>

      <RenderWhen condition={score === 'LessActive'}>
        <IconStarSolid fill={STAR_COLOR_ON} {...size} />
        <IconStarSolid fill={STAR_COLOR_OFF} {...size} />
        <IconStarSolid fill={STAR_COLOR_OFF} {...size} />
      </RenderWhen>

      <RenderWhen condition={score === 'Active'}>
        <IconStarSolid fill={STAR_COLOR_ON} {...size} />
        <IconStarSolid fill={STAR_COLOR_ON} {...size} />
        <IconStarSolid fill={STAR_COLOR_OFF} {...size} />
      </RenderWhen>

      <RenderWhen condition={score === 'VeryActive'}>
        {stars.map((_, i) => (
          <IconStarSolid key={i} fill={STAR_COLOR_ON} {...size} />
        ))}
      </RenderWhen>
    </Flex>
  )
}

export default EngagementView
